"use client";

import { useLocale } from "next-intl";
import { useEffect, useState } from "react";

import { hasAnalyticsConsent } from "./consent";
import {
  EXPERIMENT_CONTROL_VARIANT,
  type ExperimentVariantKey,
} from "./experiments.constants";
import {
  getPosthogClient,
  initializeMarketingAnalytics,
  isPosthogReady,
} from "./init";

export {
  EXPERIMENT_CONTROL_VARIANT,
  EXPERIMENT_OTP_VARIANT,
  EXPERIMENT_TRUST_VARIANT,
  WEBSITE_VOC_MESSAGING_FLAG,
  type ExperimentVariantKey,
  type WebsiteExperimentFlagKey,
} from "./experiments.constants";

const WEBSITE_FLAG_PREFIX = "website-";

const normalizeVariant = (
  value: string | boolean | undefined,
): ExperimentVariantKey => {
  if (typeof value === "string" && value.length > 0) {
    return value;
  }
  // Multivariate-only flag: ignore boolean true from misconfigured flags.
  return EXPERIMENT_CONTROL_VARIANT;
};

/**
 * Read a multivariate website experiment variant from PostHog.
 * Returns control when: no consent, PostHog not ready, or flag inactive/missing.
 */
export const readExperimentVariant = (
  flagKey: string,
): ExperimentVariantKey => {
  if (typeof window === "undefined") {
    return EXPERIMENT_CONTROL_VARIANT;
  }
  if (!hasAnalyticsConsent() || !isPosthogReady()) {
    return EXPERIMENT_CONTROL_VARIANT;
  }

  const posthog = getPosthogClient();
  if (!posthog) {
    return EXPERIMENT_CONTROL_VARIANT;
  }

  return normalizeVariant(posthog.getFeatureFlag(flagKey));
};

/** Active `website-*` flag assignments for CTA / pageview enrichment. */
export const getActiveWebsiteExperimentProperties = (): Record<
  string,
  string
> => {
  if (
    typeof window === "undefined" ||
    !hasAnalyticsConsent() ||
    !isPosthogReady()
  ) {
    return {};
  }

  const posthog = getPosthogClient();
  if (!posthog?.featureFlags) {
    return {};
  }

  const variants = posthog.featureFlags.getFlagVariants?.() ?? {};
  const properties: Record<string, string> = {};

  for (const [key, value] of Object.entries(variants)) {
    if (!key.startsWith(WEBSITE_FLAG_PREFIX)) {
      continue;
    }
    if (typeof value === "string") {
      properties[`experiment_${key}`] = value;
    } else if (typeof value === "boolean") {
      properties[`experiment_${key}`] = value
        ? "true"
        : EXPERIMENT_CONTROL_VARIANT;
    }
  }

  return properties;
};

/**
 * Subscribe to a website experiment variant. English-only; other locales always get control.
 * Defaults to control until flags load (SEO / no-consent / flicker-safe).
 */
export const useExperimentVariant = (flagKey: string): ExperimentVariantKey => {
  const locale = useLocale();
  const [variant, setVariant] = useState<ExperimentVariantKey>(
    EXPERIMENT_CONTROL_VARIANT,
  );

  useEffect(() => {
    if (locale !== "en") {
      setVariant(EXPERIMENT_CONTROL_VARIANT);
      return;
    }

    if (!hasAnalyticsConsent()) {
      setVariant(EXPERIMENT_CONTROL_VARIANT);
      return;
    }

    let cancelled = false;
    let subscribed = false;

    const apply = () => {
      if (cancelled) {
        return;
      }
      setVariant(readExperimentVariant(flagKey));
    };

    // Reads the flag and follows later updates. Runs after the first start-up
    // and again after re-consent, since a start-up cancelled by a withdrawal
    // leaves no client to subscribe to.
    const subscribe = () => {
      if (cancelled) {
        return;
      }
      const posthog = getPosthogClient();
      apply();
      if (!posthog || subscribed) {
        return;
      }
      subscribed = true;
      posthog.onFeatureFlags(apply);
    };

    void initializeMarketingAnalytics().then(subscribe);

    const onConsentChange = () => {
      if (!hasAnalyticsConsent()) {
        setVariant(EXPERIMENT_CONTROL_VARIANT);
        return;
      }
      void initializeMarketingAnalytics().then(subscribe);
    };

    window.addEventListener("rivetai:cookie-consent", onConsentChange);
    return () => {
      cancelled = true;
      window.removeEventListener("rivetai:cookie-consent", onConsentChange);
    };
  }, [flagKey, locale]);

  return variant;
};
